<template>
  <parts-password-protected-form
    v-if="! pageData?.databaseId && passwordProtectedData?.isPasswordProtected"
    :post-id="passwordProtectedData.postId"
    @access-successful="passwordAccessSuccessful"
  />
  <not-found v-if="notFound" />
  <parts-page-content v-else :page-data="pageData" :is-preview="isPreview"/>
</template>

<script async setup lang="ts">
import { getPageContentFromUri } from "~/composables/page";
import { fetchPostPreviewById } from '~/hooks/queries/use-page-data-query.js';
import NotFound from '~/components/NotFound.vue';
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  navigateTo,
  reloadNuxtApp,
  setResponseStatus,
  useRequestEvent,
  useRoute
} from "#imports";

const pageData = ref(null);
const pageLoaded = ref(false);
const route = useRoute();
const requestEvent = import.meta.server ? useRequestEvent() : null;
const isPreview = ref(false)
const passwordProtectedData = ref({})
const loadFailed = ref(false)
const notFound = computed(() => pageLoaded.value && ! Object.keys(pageData.value ?? {}).length)

if (route?.query?.preview_id) {
  isPreview.value = true;
}

const getPageContent = async () => {
  pageLoaded.value = false;
  const { data, failed } = await getPageContentFromUri(route.path);

  loadFailed.value = failed;

  if (data?.isPasswordProtected) {
    passwordProtectedData.value = {
      isPasswordProtected: data.isPasswordProtected,
      postId: data.postId,
    }
  }

  pageData.value = data;
  pageLoaded.value = true;
}

const goToRelative = (eventData) => {
  navigateTo(eventData.detail.relative_permalink);
}

const passwordAccessSuccessful = () => {
  reloadNuxtApp()
}

onMounted(async () => {
  window.addEventListener('goto-relative', goToRelative);

  if (isPreview.value) {
    try {
      const { postPreview } = await fetchPostPreviewById({ postId: parseInt(route.query.preview_id, 10), isPreview: true });

      pageData.value = postPreview;
      pageLoaded.value = true;
    } catch (err) {
      console.error(err)
      pageLoaded.value = true;
      isPreview.value = false;
    }
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('goto-relative', goToRelative);
});

if (! isPreview.value) {
  await getPageContent();

  // Crawlers need a real 404 status; the URL stays and the not-found template renders in place.
  // A failed backend fetch gets 503 so crawlers retry instead of dropping the URL.
  if (requestEvent && notFound.value) {
    setResponseStatus(requestEvent, loadFailed.value ? 503 : 404);
  }
}

watch(
  () => route.path,
  getPageContent
)
</script>
